import "./serviceRequest.css";
import { useMemo, useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import RightSideModal from "../Modal/right-side-modal";
import Card from "../card";
import { FormProvider, useForm } from 'react-hook-form';
import SelectField from "../form-fields/SelectField";
import DescriptionTextarea from "../form-fields/DescriptionTextarea";
import MultipleFileUploader from "../CustomUploader/MultipleFileUploader";
import {
  JPEG_PNG_EXTENSIONS,
  JPEG_PNG_MIME_TYPES,
} from "../CustomUploader/imageUploadConstants";
import Radio from "../form-fields/radio";
import apiHelper from "../../utils/apiHelper";
import { DocumentEntityTypesEnum, DocumentService, DocumentTypesEnum, LookupService, LookupTypesEnum, PropertyService, ServiceRequestEntityTypesEnum, ServiceRequestService, UnitService } from "@propertelligent/client-api";
import { UserLoginData } from "../../components/common/charts/Enums";
import toastServiceRequestIcons from "../../assets/toastServiceRequestIcons.svg";
import RadioGroup from "../form-fields/RadioGroup";

interface Option {
  Id: string | number;
  Name: string;
}

interface ServiceRequestValues {
  entityTypeId: string;
  serviceType: number;
  entityId: number;
  serviceSubType: number;
  description: string;
  requestDate: Date;
  documents_serviceRequestDocuments?: File[];
}

interface AddServiceRequestFormProps {
  isModalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  fetchData?: (page: number) => void;
  isEntity?: boolean;
  entityTypeId?: ServiceRequestEntityTypesEnum;
  entityId?: number;
}

const AddServiceRequestForm: React.FC<AddServiceRequestFormProps> = ({
  isModalOpen,
  setModalOpen,
  fetchData,
  isEntity,
  entityTypeId,
  entityId
}) => {
  const [resetCounter, setResetCounter] = useState(0);
  const [serviceTypes, setServiceTypes] = useState<Option[]>([]);
  const [serviceSubTypes, setServiceSubTypes] = useState<Option[]>([]);
  const [properties, setProperties] = useState<Option[]>([]);
  const [units, setUnits] = useState<Option[]>([]);
  const [isSubTypeHidden, setIsSubTypeHidden] = useState(false);
  const [isSubTypeDisabled, setIsSubTypeDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const memoizedServiceTypes = useMemo(() => serviceTypes, [serviceTypes]);
  const memoizedProperties = useMemo(() => properties, [properties]);
  const memoizedUnits = useMemo(() => units, [units]);

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      const [serviceTypesData, propertiesData, unitsData] = await Promise.all([
        fetchLookupData(ServiceRequestService.getApiServiceRequestServiceTypes),
        fetchPropertyData(),
        fetchUnitData()
      ]);

      setServiceTypes(serviceTypesData);
      setProperties(propertiesData);
      setUnits(unitsData);
    } catch (error) {
      console.error("Error loading initial data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchLookupData = useCallback(async (service: any) => {
    const response = await apiHelper(
      service,
      { showNotification: false }
    );
    return response?.data?.success?.map((val: any) => ({
      Id: val.id || val.Id || 0,
      Name: val.description || val.Name || val.name || ''
    })) || [];
  }, []);

  const fetchPropertyData = useCallback(async () => {
    const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');
    const response = await apiHelper(
      PropertyService.getApiPropertyOrganizationlookup,
      { showNotification: false },
      orgId
    );
    return response?.data?.success?.map((val: any) => ({
      Id: val.id || val.Id || 0,
      Name: val.description || val.Name || val.name || ''
    })) || [];
  }, []);

  const fetchUnitData = useCallback(async () => {
    const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id) || '0');
    const response = await apiHelper(
      UnitService.getApiUnitOrganizationlookup,
      { showNotification: false },
      orgId
    );
    return response?.data?.success?.map((val: any) => ({
      Id: val.id || val.Id || 0,
      Name: val.description || val.Name || val.name || ''
    })) || [];
  }, []);

  const methods = useForm<ServiceRequestValues>({
    mode: "onChange",
    defaultValues: {
      entityTypeId: isEntity && entityTypeId !== undefined
        ? entityTypeId.toString()
        : ServiceRequestEntityTypesEnum.Property.toString(),
      serviceType: null as any,
      entityId: isEntity && entityId !== undefined ? entityId : null as any,
      serviceSubType: null as any,
      description: '',
      requestDate: new Date(),
      documents_serviceRequestDocuments: []
    }
  });
  const { formState: { isDirty, isValid }, getValues, watch } = methods;

  // Un-debounced core fetch, kept separate so a future awaited call site (e.g. an initial-load path)
  // can't race methods.reset() the way EditServiceRequestForm's debounced fetch once did (caught by
  // ui-code-writer review on bug/service-request-subtype) - debounce is only appropriate for the
  // interactive handleServiceTypeChange path below, not a one-shot awaited load.
  const loadServiceSubTypes = useCallback(async (value: number) => {
    setIsSubTypeDisabled(true);
    try {
      const response = await apiHelper(
        ServiceRequestService.getApiServiceRequestServiceSubTypes,
        { showNotification: false },
        value
      );
      const data = response?.data?.success?.map((item: any) => ({
        Id: item.id || item.Id || 0,
        Name: item.name || item.Name || item.description || ''
      })) || [];
      setServiceSubTypes(data);
      return data as Option[];
    } catch (err) {
      console.error("Error fetching service sub-types:", err);
      setServiceSubTypes([]);
      return [] as Option[];
    } finally {
      setIsSubTypeDisabled(false);
    }
  }, []);

  // Drops the current serviceSubType value if it doesn't belong to the freshly-fetched category
  // options - the single reconciliation point for the debounced interactive path below.
  const reconcileServiceSubType = useCallback((data: Option[]) => {
    const validIds = new Set(data.map((opt) => Number(opt.Id)));
    const currentValue = methods.getValues("serviceSubType");
    if (currentValue != null && !validIds.has(Number(currentValue))) {
      methods.setValue("serviceSubType", null as any, { shouldValidate: true });
    }
  }, [methods]);

  const fetchServiceSubTypes = useCallback(
    debounce((value: number) => {
      loadServiceSubTypes(value).then(reconcileServiceSubType);
    }, 300),
    [loadServiceSubTypes, reconcileServiceSubType]
  );

  const handleServiceTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = Number(e.target.value);
    setServiceSubTypes([]);
    methods.setValue("serviceSubType", null as any, { shouldValidate: true });
    switch (value) {
      case 999:
        setIsSubTypeDisabled(true);
        setIsSubTypeHidden(true);
        break;
      default:
        setIsSubTypeHidden(false);
        setIsSubTypeDisabled(false);
        fetchServiceSubTypes(value);
    }
  }, [fetchServiceSubTypes, methods]);

  useEffect(() => {
    if (isModalOpen) {
      methods.reset({
        entityTypeId: isEntity && entityTypeId !== undefined
          ? entityTypeId.toString()
          : ServiceRequestEntityTypesEnum.Property.toString(),
        serviceType: null as any,
        entityId: isEntity && entityId !== undefined ? entityId : null as any,
        serviceSubType: null as any,
        description: '',
        requestDate: new Date(),
        documents_serviceRequestDocuments: []
      });
      fetchInitialData();
    }

    return () => {
      fetchServiceSubTypes.cancel();
    };
  }, [isModalOpen, fetchInitialData, fetchServiceSubTypes, methods, isEntity, entityTypeId, entityId]);

  const onSubmit = async (data: ServiceRequestValues) => {
    try {
      setIsLoading(true);

      const documents = getValues('documents_serviceRequestDocuments');

      // Find selected names for dynamic message
      const selectedServiceType = serviceTypes.find(
        (st) => Number(st.Id) === Number(data.serviceType)
      );
      const serviceTypeName = selectedServiceType?.Name || 'Service';

      let entityName = 'Unknown Location';

      if (entityType === ServiceRequestEntityTypesEnum.Property.toString()) {
        const selectedProperty = properties.find(
          (p) => Number(p.Id) === Number(data.entityId)
        );
        entityName = selectedProperty?.Name || 'Property';
      } else if (entityType === ServiceRequestEntityTypesEnum.Unit.toString()) {
        const selectedUnit = units.find(
          (u) => Number(u.Id) === Number(data.entityId)
        );
        entityName = selectedUnit?.Name || 'Unit';
      }

      const dynamicSuccessMessage = `
        <strong>${serviceTypeName}</strong> added in 
        <strong>${entityName}</strong> successfully
      `;

      const requestData = {
        entityTypeId: Number(data.entityTypeId),
        serviceType: Number(data.serviceType),
        serviceSubType:
          Number(data.serviceType) === 999 ? 0 : (data.serviceSubType ? Number(data.serviceSubType) : null),
        entityId: Number(data.entityId),
        description: data.description,
        requestDate: data.requestDate
      };

      const servReqRes = await apiHelper(
        ServiceRequestService.postApiServiceRequest,
        {
          successMessage: dynamicSuccessMessage,
          showNotification: true,
          title: 'Service Request Added',
          successIcon: <img src={toastServiceRequestIcons} alt="success" width="20" height="20" />
        },
        requestData
      );

      const serviceRequestId = servReqRes.data.success.id;

      // Handle document uploads
      if (documents && Array.isArray(documents) && documents.length > 0) {
        for (const file of documents) {
          const apiDocObj = {
            EntityTypeId: DocumentEntityTypesEnum.ServiceRequest,
            EntityId: serviceRequestId,
            DocumentTypeId: DocumentTypesEnum.BeforeImage,
            IsMain: false,
            FileName: file.name,
            Document: file
          };

          await apiHelper(
            DocumentService.postApiDocument,
            { showNotification: false },
            apiDocObj
          );
        }
      }

      methods.reset();
      setResetCounter((prev) => prev + 1);
      setModalOpen(false);
      fetchData?.(1);
    } catch (error) {
      console.error("Error submitting service request:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const entityType = methods.watch("entityTypeId");

  return (
    <RightSideModal
      isOpen={isModalOpen}
      onClose={() => setModalOpen(false)}
      title="Add Service Request"
      buttonlabel="Save"
      formId="ServiceRequestForm"
      maxWidth={550}
      isDirty={isDirty}
      isSubmitDisabled={isLoading || !isDirty || !isValid}
      isLoading={methods.formState.isSubmitting}
    >
      <Card className="no-bg">
        <FormProvider {...methods}>
          <form id="ServiceRequestForm" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="row">
              {!isEntity && (
                <div className="col-md-12">
                  <RadioGroup
                    label="Create Service Request For"
                    required
                    requiredMessage="Please select one type"
                  >
                    <Radio
                      name="entityTypeId"
                      value={ServiceRequestEntityTypesEnum.Property.toString()}
                      label="Property"
                      isDisabled={isEntity}
                    />
                    <Radio
                      name="entityTypeId"
                      value={ServiceRequestEntityTypesEnum.Unit.toString()}
                      label="Unit"
                      isDisabled={isEntity}
                    />
                  </RadioGroup>
                </div>
              )}

              {entityType === ServiceRequestEntityTypesEnum.Unit.toString() && 
                (!(isEntity && entityTypeId === ServiceRequestEntityTypesEnum.Unit)) && (
                <div className="col-md-12">
                  <SelectField
                    placeholderText="Select a Unit"
                    name="entityId"
                    label="Unit"
                    required
                    requiredMessage="Please select a Unit"
                    options={memoizedUnits}
                    disabled={isLoading || (isEntity && entityTypeId === ServiceRequestEntityTypesEnum.Unit)}
                  />
                </div>
              )}

              {entityType === ServiceRequestEntityTypesEnum.Property.toString() && (
                <div className="col-md-12">
                  <SelectField
                    placeholderText="Select a Property"
                    name="entityId"
                    label="Property"
                    required
                    requiredMessage="Please select a property"
                    options={memoizedProperties}
                    disabled={isLoading || (isEntity && entityTypeId === ServiceRequestEntityTypesEnum.Property)}
                    isSearchable={true}

                  />
                </div>
              )}

              <div className="col-md-6">
                <SelectField
                  name="serviceType"
                  label="Service Type"
                  required
                  requiredMessage="Please select a service type"
                  options={memoizedServiceTypes}
                  onChange={handleServiceTypeChange}
                  disabled={isLoading}
                />
              </div>

              {!isSubTypeHidden && (
                <div className="col-md-6">
                  <SelectField
                    name="serviceSubType"
                    label="Sub Type"
                    required={!isSubTypeDisabled}
                    requiredMessage="Please select a sub type"
                    options={serviceSubTypes}
                    disabled={isSubTypeDisabled || isLoading}
                  />
                </div>
              )}

              <div className="col-md-12">
                <DescriptionTextarea
                  name="description"
                  requiredMessage="Description is required"
                  rows={3}
                  required
                  disabled={isLoading}
                  placeholder="Enter Description"
                  validation={{
                    maxLength: { value: 250, message: "Description cannot exceed 250 characters" }
                  }}
                />
              </div>

              <div className="col-md-12">
                <MultipleFileUploader
                  name="documents_serviceRequestDocuments"
                  uploaderId="serviceRequestDocuments"
                  label="Upload Documents"
                  required={false}
                  disabled={isLoading}
                  allowedFormats={[...JPEG_PNG_MIME_TYPES]}
                  allowedExtensions={[...JPEG_PNG_EXTENSIONS]}
                  maxSizeMB={1}
                  maxFiles={5}
                  resetTrigger={resetCounter}
                  uploadertext="Upload Files"
                  uploadertypeslabel="Max. size 1MB. JPEG, PNG (Max 5 files)"
                  convertImagesToPng={false}
                />
              </div>
            </div>
          </form>
        </FormProvider>
      </Card>
    </RightSideModal>
  );
};

export default AddServiceRequestForm;